import { useEffect, useState } from 'react';
import { api, type ModelActivity } from '../../api/client';

// Registrar's read-only window into the open Quota Model app: whether the window
// is open, who has taken part, and what policies they've saved/run. Opened from
// the "Model Activity" button on the Quota page. Read-only; mutates nothing.

const dt = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' });
};

export function ModelActivityModal({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<ModelActivity | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .quotaModelActivity()
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      live = false;
    };
  }, []);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop click-to-close duplicates the explicit Close button inside
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 16,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- click handler only stops backdrop-close propagation, no action of its own */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="panel"
        style={{
          width: 'min(760px, 100%)',
          maxHeight: '86vh',
          overflowY: 'auto',
          padding: 0,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '18px 24px',
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            background: 'var(--white)',
            zIndex: 1,
          }}
        >
          <h2 style={{ fontSize: 17, fontWeight: 600, color: 'var(--blue)', margin: 0 }}>
            Quota Model — Activity
          </h2>
          {data &&
            (data.open ? (
              <span className="pill pill-success">Window open</span>
            ) : (
              <span className="pill pill-neutral">Window closed</span>
            ))}
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ marginLeft: 'auto' }}>
            Close
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {error && (
            <p style={{ color: 'var(--red)', fontSize: 14 }}>Couldn’t load activity: {error}</p>
          )}
          {!data && !error && <p style={{ color: 'var(--text2)', fontSize: 14 }}>Loading…</p>}

          {data && !data.bound && (
            <p style={{ color: 'var(--text2)', fontSize: 14 }}>
              The model database isn’t bound in this environment, so there’s nothing to show.
            </p>
          )}

          {data && data.bound && (
            <>
              {/* Counts strip */}
              <div style={{ display: 'flex', gap: 28, marginBottom: 22, flexWrap: 'wrap' }}>
                <Counter n={data.participants.length} label="Participants" />
                <Counter n={data.policies.length} label="Saved policies" />
                <Counter n={data.runs.length} label="Recent runs" />
              </div>

              {/* Participants */}
              <Section title="Who has taken part">
                {data.participants.length === 0 ? (
                  <Empty>No one has signed in yet.</Empty>
                ) : (
                  <table className="grand-total-table" style={{ marginTop: 0 }}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Church</th>
                        <th className="right">Policies</th>
                        <th>Last seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.participants.map((p) => (
                        <tr key={p.email}>
                          <td>
                            {p.name}
                            {p.role === 'admin' && (
                              <span className="pill pill-brand" style={{ marginLeft: 8 }}>
                                registrar
                              </span>
                            )}
                            <div style={{ fontSize: 12, color: 'var(--text2)' }}>{p.email}</div>
                          </td>
                          <td>{p.church ?? '—'}</td>
                          <td className="right">{p.policyCount}</td>
                          <td>{dt(p.lastSeenAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              {/* Saved policies */}
              <Section title="Policies saved">
                {data.policies.length === 0 ? (
                  <Empty>No participant has saved a policy yet.</Empty>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {data.policies.map((p, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '9px 0',
                          borderBottom: '1px solid var(--border-soft)',
                          fontSize: 13,
                        }}
                      >
                        <span>
                          <strong>{p.name}</strong>{' '}
                          <span style={{ color: 'var(--text2)' }}>
                            — {p.who}
                            {p.church ? `, ${p.church}` : ''}
                          </span>
                        </span>
                        <span style={{ color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                          {dt(p.updatedAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Runs */}
              <Section title="Policies run">
                {data.runs.length === 0 ? (
                  <Empty>No runs recorded yet.</Empty>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {data.runs.map((r, i) => (
                      <li
                        key={i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 12,
                          padding: '9px 0',
                          borderBottom: '1px solid var(--border-soft)',
                          fontSize: 13,
                        }}
                      >
                        <span>
                          <strong>{r.who}</strong>
                          {r.detail && <span style={{ color: 'var(--text2)' }}> — {r.detail}</span>}
                        </span>
                        <span style={{ color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                          {dt(r.at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              <p style={{ fontSize: 11, color: 'var(--text2)', marginTop: 18 }}>
                Read-only snapshot · {dt(data.generatedAt)}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Counter({ n, label }: { n: number; label: string }) {
  return (
    <div>
      <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--blue)', lineHeight: 1 }}>{n}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3
        style={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--brand)',
          marginBottom: 10,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 13, color: 'var(--text2)', fontStyle: 'italic', margin: 0 }}>
      {children}
    </p>
  );
}
